import { Injectable, Inject, DOCUMENT } from '@angular/core';


@Injectable({
  providedIn: 'root'
})
export class AccessibilityObserverService {
  private observer!: MutationObserver;

  constructor(@Inject(DOCUMENT) private document: Document) { }

  public start(): void {
    this.initObserver();
  }

  private initObserver(): void {
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.applyWcagFixes(node);
              node.querySelectorAll('*').forEach((child) => {
                if (child instanceof HTMLElement) {
                  this.applyWcagFixes(child);
                }
              });
            }
          });
        }
        else if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement && mutation.attributeName) {
          this.handleAttributeMutation(mutation.target, mutation.attributeName);
        }
      }
    });

    this.observer.observe(this.document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-valuenow', 'aria-activedescendant']
    });
  }

  private handleAttributeMutation(target: HTMLElement, attr: string) {
    switch (attr) {
      case 'aria-valuenow':
        this.fixInvalidAriaValues(target);
        break;

      case 'aria-activedescendant':
        this.fixInvalidAriaActivedescendant(target);
        break;
    }
  }

  private applyWcagFixes(element: HTMLElement): void {
    this.fixTextAlternatives(element);
    this.fixPrimeNgComponents(element);
    this.fixGenericInputs(element);
    this.fixDropdownRoles(element);
    this.fixNestedFocusableElements(element);
    this.fixAccessibleNames(element);
    this.fixInvalidAriaActivedescendant(element);
    this.fixInvalidAriaValues(element);
  }

  // PrimeNG component accessibility fixes
  private fixPrimeNgComponents(el: HTMLElement): void {

    // p-checkbox
    if (el.classList.contains('p-checkbox')) {
      const box = el.querySelector('.p-checkbox-box');
      const input = el.querySelector('input[type="checkbox"]');
      const parentInteractive = el.closest('[role="option"], [role="row"], [role="button"], [role="link"]');
      const isNestedInInteractive = parentInteractive && parentInteractive !== el;
      if (box) {
        if (!isNestedInInteractive) {
          if (!box.hasAttribute('role')) {
            box.setAttribute('role', 'checkbox');
          }
          if (!box.hasAttribute('tabindex')) {
            box.setAttribute('tabindex', '0');
          }
          if (!box.hasAttribute('aria-checked')) {
            box.setAttribute('aria-checked', (input as HTMLInputElement)?.checked ? 'true' : 'false');
          }
        } else {
          box.removeAttribute('role');
          box.removeAttribute('tabindex');
          box.removeAttribute('aria-checked');
        }
        if (!box.hasAttribute('aria-label')) {
          const label = input?.getAttribute('aria-label') || 'checkbox option';
          box.setAttribute('aria-label', label);
        }
      }
    }

    // p-dropdown
    if (el.classList.contains('p-dropdown')) {
      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'combobox');
      }
      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'listbox');
      }
      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'dropdown menu');
      }
    }

    // p-overlayPanel Dialog and p-confirmDialog Alert Dialog fix
    if (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog') {
      if (!el.hasAttribute('aria-modal')) {
        el.setAttribute('aria-modal', 'true');
      }

      const hasLabel = el.hasAttribute('aria-label');
      const hasLabelledBy = el.hasAttribute('aria-labelledby');

      if (!hasLabel && !hasLabelledBy) {
        const heading = el.querySelector('h1, h2, h3, .p-dialog-title');
        if (heading) {
          const headingId = heading.id || `dialog-heading-${Math.random().toString(36).substring(2, 8)}`;
          heading.id = headingId;
          el.setAttribute('aria-labelledby', headingId);
        } else {
          el.setAttribute('aria-label', 'Confirmation Dialog');
        }
      }      
    }
  }

  // Fix PrimeNG dropdown ARIA roles
  private fixDropdownRoles(element: HTMLElement): void {
    const wrappers = element.querySelectorAll('.p-select-list-container');

    wrappers.forEach(wrapper => {
      if (wrapper.getAttribute('role') === 'listbox') {
        wrapper.removeAttribute('role');
        wrapper.setAttribute('role', 'presentation');
      }

      if (!wrapper.hasAttribute('tabindex')) {
        wrapper.setAttribute('tabindex', '0');
      }

      const ul = wrapper.querySelector('ul');
      if (ul && !ul.hasAttribute('role')) {
        ul.setAttribute('role', 'listbox');
      }

      const liElements = ul?.querySelectorAll('li') || [];
      liElements.forEach(li => {
        if (!li.hasAttribute('role')) {
          li.setAttribute('role', 'option');
        }
      });
    });
  }

  //PrimeNG accessible name fix and empty message fix
  private fixAccessibleNames(element: HTMLElement): void {
    const applyFixes = () => {
      const listboxes = element.querySelectorAll('[role="listbox"]');

      listboxes.forEach(listbox => {
        const hasAriaLabel = listbox.hasAttribute('aria-label') && listbox.getAttribute('aria-label')?.trim() !== '';
        const hasAriaLabelledBy = listbox.hasAttribute('aria-labelledby') && listbox.getAttribute('aria-labelledby')?.trim() !== '';

        if (!hasAriaLabel && !hasAriaLabelledBy) {
          listbox.setAttribute('aria-label', 'Selectable list of options');
        }

        const emptyMessageItems = listbox.querySelectorAll('.p-listbox-empty-message');
        emptyMessageItems.forEach(item => {
          item.setAttribute('role', 'option');
          item.setAttribute('aria-disabled', 'true');
          item.removeAttribute('tabindex');
        });

        const liItems = listbox.querySelectorAll('li');
        liItems.forEach(li => {
          if (
            !li.classList.contains('p-listbox-empty-message') &&
            !li.classList.contains('p-multiselect-item-group')
          ) {
            li.setAttribute('role', 'option');
          }
          if (li.classList.contains('p-multiselect-item-group') && li.getAttribute('role') !== 'presentation') {
            li.setAttribute('role', 'presentation');
          }
        });
      });
    };

    applyFixes();

    const observer = new MutationObserver(applyFixes);
    observer.observe(element, { childList: true, subtree: true });
  }

  private fixNestedFocusableElements(element: HTMLElement): void {
    const listboxItems = element.querySelectorAll('li[role="option"]');
    listboxItems.forEach(item => {
      const focusableChildren = item.querySelectorAll('[tabindex="0"], [role="checkbox"]');
      focusableChildren.forEach(child => {
        child.removeAttribute('tabindex');
        if (child.getAttribute('role') === 'checkbox') {
          child.setAttribute('aria-hidden', 'true');
        }
      });
    });

    setTimeout(() => {
      const listboxItems = element.querySelectorAll('li[role="option"]');

      listboxItems.forEach(item => {
        const focusableChildren = item.querySelectorAll('[tabindex="0"], [role="checkbox"]');

        focusableChildren.forEach(child => {
          child.removeAttribute('tabindex');
          if (child.getAttribute('role') === 'checkbox') {
            child.setAttribute('aria-hidden', 'true');
          }
        });
      });
    }, 1000);
  }

  //Generic input fixes
  private fixGenericInputs(el: HTMLElement): void {
    if (el.tagName === 'INPUT') {
      const name = el.getAttribute('formcontrolname') || el.getAttribute('name') || 'input field';
      if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', name);
      if (!el.hasAttribute('placeholder')) el.setAttribute('placeholder', `Enter ${name}`);
      if (!el.hasAttribute('title')) el.setAttribute('title', name);

      const id = el.id || `input-${Math.random().toString(36).substring(2, 8)}`;
      el.id = id;
      const label = el.closest('.field')?.querySelector('label');
      if (label) {
        if (!label.hasAttribute('for')) label.setAttribute('for', id);
        el.setAttribute('aria-labelledby', label.textContent?.trim() || name);
      }
    }
  }

  //  General WCAG fixes
  private fixTextAlternatives(el: HTMLElement): void {
    if (el.tagName === 'IMG' && !el.hasAttribute('alt')) {
      el.setAttribute('alt', 'decorative');
    }
    if ((el.tagName === 'BUTTON' || el.tagName === 'A') && el.textContent?.trim() === '') {
      el.setAttribute('aria-label', 'action');
    }
  }

  private fixInvalidAriaValues(el: HTMLElement): void {
    if (el.hasAttribute('aria-valuenow')) {
      const role = el.getAttribute('role') || '';
      const allowedRoles = ['spinbutton', 'slider', 'progressbar'];
      if (!allowedRoles.includes(role)) {
        el.removeAttribute('aria-valuenow');
      } else {
        const val = el.getAttribute('aria-valuenow');
        if (val === 'null' || val === '') {
          const currentValue = (el as HTMLInputElement).value || '0';
          el.setAttribute('aria-valuenow', currentValue);
        }
      }
    }
  }

  private fixInvalidAriaActivedescendant(el: HTMLElement): void {
    if (el.hasAttribute('aria-activedescendant')) {
      const targetId = el.getAttribute('aria-activedescendant')!;
      if (!this.document.getElementById(targetId)) {
        el.removeAttribute('aria-activedescendant');
      }
    }
  }
}
